import { FormAnswersMap } from "src/components/Providers/FormProvider";
import * as AF from "src/types/formTemplate";
import {
  QuestionRule,
  Rule,
  SchoolRule,
  Visibility,
} from "src/types/formTemplate/visibility";

type key = string;
export type VisibilityContext = {
  sortedQuestionIds: uuid[];
  visibilityMap: Map<uuid, Visibility>;
  formAnswersMap: FormAnswersMap;
  questionKeysMap: Map<key, uuid>;
  optionsMap: Map<uuid, key>;
  selectedSchoolsSet: Set<key>;
  questionsWithVerifiedAnswers: Set<uuid>;
};

export function findQuestionsNeedingAnswerCleanup(
  questions: { id: uuid }[],
  visibilityContext: VisibilityContext
): { id: uuid }[] {
  return questions.filter((q) => {
    return (
      !isVisible(q.id, visibilityContext) &&
      // Don't clear out answers to school specific question to match legacy school specific question behavior
      // See: https://github.com/Avela-Education/apply-frontend/pull/2899
      !isSchoolSpecificQuestion(q.id, visibilityContext) &&
      !answerIsVerified(q.id, visibilityContext)
    );
  });
}

export function answerIsVerified(
  questionId: uuid,
  visibilityContext: VisibilityContext
): boolean {
  return visibilityContext.questionsWithVerifiedAnswers.has(questionId);
}

export function isSchoolSpecificQuestion(
  questionId: uuid,
  visibilityContext: Pick<VisibilityContext, "visibilityMap">
): boolean {
  const visibility = visibilityContext.visibilityMap.get(questionId);
  if (!visibility) {
    return false;
  }

  const rulesList =
    visibility.allOf ?? visibility.anyOf ?? visibility.oneOf ?? [];

  // find rule that are not school
  let nonSchoolRuleFound = false;

  findNotSchoolRule: for (const { rules } of rulesList) {
    for (const rule of rules) {
      if (rule.dataType !== "school") {
        nonSchoolRuleFound = true;
        break findNotSchoolRule;
      }
    }
  }

  return !nonSchoolRuleFound;
}

export function isVisible(
  questionId: uuid,
  visibilityContext: VisibilityContext
): boolean {
  const formAnswersMap = removeInvisibleAnswers(visibilityContext);
  return isVisibleHelper(questionId, { ...visibilityContext, formAnswersMap });
}

function isVisibleHelper(
  questionId: uuid,
  visibilityContext: VisibilityContext
): boolean {
  const { visibilityMap } = visibilityContext;
  const visibility = visibilityMap.get(questionId);

  if (visibility === null || visibility === undefined) {
    return true;
  }

  // Helper function to evaluate a single rule
  const evaluateRule = (rule: Rule): boolean => {
    switch (rule.dataType) {
      case "questionKey": {
        return evaluateQuestionKeyRule(rule, visibility, visibilityContext);
      }
      case "school": {
        return evaluateSchoolRule(rule, visibilityContext);
      }
    }
  };

  // Helper function to evaluate a group of rules
  const evaluateGroup = (group: { rules: Rule[] }) => {
    return group.rules.every((rule) => evaluateRule(rule));
  };

  if (visibility.allOf) {
    // Logical AND - all groups must be satisfied
    const allGroupsSatisfied = visibility.allOf.every(evaluateGroup);
    return allGroupsSatisfied
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  } else if (visibility.anyOf) {
    // Logical OR - any group must be satisfied
    const anyGroupSatisfied = visibility.anyOf.some(evaluateGroup);
    return anyGroupSatisfied
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  } else if (visibility.oneOf) {
    // Logical XOR - exactly one group must be satisfied
    const satisfiedGroups = visibility.oneOf.filter(evaluateGroup);
    return satisfiedGroups.length === 1
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  }

  if (visibility.result === undefined) {
    return visibility.default === "VISIBLE";
  }

  return visibility.result === "VISIBLE";
}

function evaluateSchoolRule(
  rule: SchoolRule,
  visibilityContext: Pick<VisibilityContext, "selectedSchoolsSet">
): boolean {
  const { selectedSchoolsSet } = visibilityContext;

  switch (rule.operator) {
    case "IN": {
      if (Array.isArray(rule.value)) {
        return rule.value.some((schoolId) => selectedSchoolsSet.has(schoolId));
      }
      return selectedSchoolsSet.has(rule.value);
    }

    case "NOTIN": {
      if (Array.isArray(rule.value)) {
        return !rule.value.some((schoolId) => selectedSchoolsSet.has(schoolId));
      }
      return !selectedSchoolsSet.has(rule.value);
    }

    case "EQUAL": {
      if (Array.isArray(rule.value)) {
        // For arrays, check exact set equality (same elements, can be different order)
        // 1. Check if sizes match
        if (selectedSchoolsSet.size !== rule.value.length) {
          return false;
        }
        // 2. Check if every value in the rule array is in the selected schools set
        return rule.value.every((schoolId) => selectedSchoolsSet.has(schoolId));
      }

      // For single value, there must be exactly one selected school that matches
      return (
        selectedSchoolsSet.size === 1 && selectedSchoolsSet.has(rule.value)
      );
    }

    case "NOTEQUAL": {
      if (Array.isArray(rule.value)) {
        // For arrays, the opposite of equality check
        if (selectedSchoolsSet.size !== rule.value.length) {
          return true;
        }
        return !rule.value.every((schoolId) =>
          selectedSchoolsSet.has(schoolId)
        );
      }

      // For single value
      return (
        selectedSchoolsSet.size !== 1 || !selectedSchoolsSet.has(rule.value)
      );
    }
    default:
      return false;
  }
}
function evaluateQuestionKeyRule(
  rule: QuestionRule,
  visibility: Visibility,
  visibilityContext: VisibilityContext
): boolean {
  const { questionKeysMap, optionsMap, formAnswersMap } = visibilityContext;

  const questionId = getQuestionId(rule.property, questionKeysMap);
  const answerId = formAnswersMap.get(questionId);
  if (answerId === undefined) {
    return visibility.default === "VISIBLE";
  }

  /**
   * Rule.value can either be key or id
   * answerId is always in id
   */
  switch (rule.operator) {
    case "IN": {
      if (Array.isArray(answerId)) {
        return answerId.some(
          (a) =>
            rule.value.includes(a) ||
            rule.value.includes(optionsMap.get(a) ?? "") // TODO: backward compatibility, remove when fully migrated to id
        );
      }
      return (
        rule.value.includes(answerId.toString()) ||
        rule.value.includes(optionsMap.get(answerId.toString()) ?? "") // TODO: backward compatibility, remove when fully migrated to id
      );
    }

    case "NOTIN": {
      if (Array.isArray(answerId)) {
        return !answerId.some(
          (a) =>
            rule.value.includes(a) ||
            rule.value.includes(optionsMap.get(a) ?? "") // TODO: backward compatibility, remove when fully migrated to id
        );
      }
      return (
        !rule.value.includes(answerId.toString()) &&
        !rule.value.includes(optionsMap.get(answerId.toString()) ?? "") // TODO: backward compatibility, remove when fully migrated to id
      );
    }

    case "EQUAL": {
      return (
        answerId.toString() === rule.value ||
        optionsMap.get(answerId.toString()) === rule.value // TODO: backward compatibility, remove when fully migrated to id
      );
    }

    case "NOTEQUAL": {
      return (
        answerId.toString() !== rule.value &&
        optionsMap.get(answerId.toString()) !== rule.value // TODO: backward compatibility, remove when fully migrated to id
      );
    }

    default:
      return false;
  }
}

/**
 * Temporary backward compatibility function to resolve questionId from either key or id
 * TODO: Remove this function once we're fully migrated to id
 */
function getQuestionId(
  idOrKey: string,
  questionKeyMap: Map<string, string>
): string {
  return questionKeyMap.get(idOrKey) ?? idOrKey;
}

/**
 * Remove all answers for invisible question. We assume that the visibility
 * dependency chain will follow the sorting order of the question.
 */
export function removeInvisibleAnswers(
  visibilityContext: VisibilityContext
): FormAnswersMap {
  const { sortedQuestionIds, formAnswersMap } = visibilityContext;
  const updatedFormAnswersMap = new Map(formAnswersMap);

  for (let i = 0; i < sortedQuestionIds.length; i++) {
    const questionId = sortedQuestionIds[i]!;
    if (!formAnswersMap.has(questionId)) {
      continue;
    }

    const isInvisible = !isVisibleHelper(questionId, {
      ...visibilityContext,
      formAnswersMap: updatedFormAnswersMap,
    });
    if (isInvisible) {
      updatedFormAnswersMap.delete(questionId);
    }
  }

  return updatedFormAnswersMap;
}

export const getVisibleQuestionsCount = (
  formTemplate: AF.FormTemplate<AF.WithId>,
  visibilityContext: VisibilityContext
) => {
  return formTemplate.sections.reduce((total: number, section) => {
    return (
      total +
      ((section && "questions" in section && section.questions) || []).filter(
        (question: AF.Question<AF.WithId>) =>
          isVisible(question.id, visibilityContext)
      ).length
    );
  }, 0);
};

/**
 * Determines if a question is visible based only on school-specific rules.
 * Unlike `isVisible`, this function only evaluates school rules and ignores question key rules.
 * It's used to check if a question should be visible for specific schools regardless of other
 * visibility conditions.
 */
export type SchoolVisibilityContext = Pick<
  VisibilityContext,
  "visibilityMap" | "selectedSchoolsSet"
>;
export function isSchoolSpecificVisible(
  questionId: uuid,
  visibilityContext: SchoolVisibilityContext
): boolean {
  const { visibilityMap } = visibilityContext;
  const visibility = visibilityMap.get(questionId);

  if (visibility === null || visibility === undefined) {
    return false;
  }

  if (!hasSchoolBasedVisibility(visibility)) {
    return false;
  }

  // Helper function to evaluate a single rule
  const evaluateRule = (rule: Rule): boolean => {
    switch (rule.dataType) {
      case "questionKey": {
        return true;
      }
      case "school": {
        return evaluateSchoolRule(rule, visibilityContext);
      }
    }
  };

  // Helper function to evaluate a group of rules
  const evaluateGroup = (group: { rules: Rule[] }) => {
    return group.rules.every((rule) => evaluateRule(rule));
  };

  if (visibility.allOf) {
    // Logical AND - all groups must be satisfied
    const allGroupsSatisfied = visibility.allOf.every(evaluateGroup);
    return allGroupsSatisfied
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  } else if (visibility.anyOf) {
    // Logical OR - any group must be satisfied
    const anyGroupSatisfied = visibility.anyOf.some(evaluateGroup);
    return anyGroupSatisfied
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  } else if (visibility.oneOf) {
    // Logical XOR - exactly one group must be satisfied
    const satisfiedGroups = visibility.oneOf.filter(evaluateGroup);
    return satisfiedGroups.length === 1
      ? visibility.result === "VISIBLE"
      : visibility.default === "VISIBLE";
  }

  if (visibility.result === undefined) {
    return false;
  }

  return false;
}

export function hasSchoolBasedVisibility(visibility: Visibility): boolean {
  const groups = visibility.allOf ?? visibility.anyOf ?? visibility.oneOf ?? [];
  for (const group of groups) {
    for (const rule of group.rules) {
      if (rule.dataType === "school") {
        return true;
      }
    }
  }

  return false;
}

export function toVisibilityMap(
  questions: AF.Question<AF.WithId>[]
): Map<string, Visibility> {
  const visibilityMap = new Map<uuid, Visibility>();
  for (const question of questions) {
    if (question.visibility === null || question.visibility === undefined) {
      continue;
    }

    visibilityMap.set(question.id, question.visibility);
  }

  return visibilityMap;
}
